import type {CSSProperties} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import type {Ticket} from '../../types/ticket';
import type {MentionUser} from '../../hooks/useMentionUsers';
import {PriorityBadge} from '../PriorityBadge';
import {formatTicketAge} from '../../utils/formatTicketAge';
import {CardKebabMenu} from './CardKebabMenu';

interface Props {
	ticket: Ticket;
	draggable: boolean;
	users?: MentionUser[];
	onAssign?: (ticketId: string, userId: string | null, note?: string) => void;
	onDelete?: (ticketId: string) => void;
}

interface TicketCardPreviewProps {
	ticket: Ticket;
	interactive?: boolean;
	className?: string;
	style?: CSSProperties;
	ariaDisabled?: boolean;
	title?: string;
	users?: MentionUser[];
	onAssign?: (userId: string | null, note?: string) => void;
	onDelete?: () => void;
	kebabDisabled?: boolean;
}

const Card = styled.article<{$interactive: boolean; $urgent: boolean}>`
	border-radius: 8px;
	background: #ffffff;
	border: 1px solid #e2e8f0;
	padding: 12px;
	transition: box-shadow 0.15s;
	box-shadow: ${p => (p.$interactive ? '0 1px 2px rgb(15 23 42 / 0.06)' : '0 4px 12px rgb(15 23 42 / 0.1)')};
	${p => p.$urgent && 'border-left: 4px solid #ef4444;'}

	&:hover {
		${p => p.$interactive && 'box-shadow: 0 4px 12px rgb(15 23 42 / 0.1);'}
	}

	/* Reveal the kebab menu trigger on card hover (replaces group-hover). */
	&:hover [data-kebab] {
		opacity: 1;
	}
`;

const TopRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 8px;
`;

const Slug = styled.span`
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font: ${p => p.theme.ticketing.font.body3};
	color: #64748b;
`;

const BadgeRow = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

const subjectCss = `
	margin-top: 8px;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	font-weight: 500;
	color: #0f172a;
`;

const SubjectLink = styled(Link)`
	${subjectCss}
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

const SubjectText = styled.span`
	${subjectCss}
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
`;

const RequesterChip = styled.div`
	margin-top: 8px;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #f1f5f9;
	padding: 2px 8px;
	font: ${p => p.theme.ticketing.font.body3};
	color: #334155;
`;

const BottomRow = styled.div`
	margin-top: 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const AssigneeAvatar = styled.span`
	display: inline-flex;
	height: 24px;
	width: 24px;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #dcd9ff;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	color: #3d36b3;
`;

const UnassignedAvatar = styled.span`
	display: inline-flex;
	height: 24px;
	width: 24px;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #f1f5f9;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	color: #64748b;
`;

const Age = styled.span`
	font: ${p => p.theme.ticketing.font.body3};
	color: #64748b;
`;

const DragWrapper = styled.div<{$draggable: boolean}>`
	cursor: ${p => (p.$draggable ? 'grab' : 'default')};
	${p => p.$draggable && '&:active { cursor: grabbing; }'}
`;

function initials(name: string | null): string {
	if (!name) return '?';
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(s => s[0]!.toUpperCase())
		.join('');
}

export function TicketCardPreview({
	ticket,
	interactive = false,
	className,
	style,
	ariaDisabled,
	title,
	users,
	onAssign,
	onDelete,
	kebabDisabled = false,
}: TicketCardPreviewProps) {
	return (
		<Card
			style={style}
			role="article"
			aria-disabled={ariaDisabled}
			title={title}
			className={className}
			$interactive={interactive}
			$urgent={ticket.priority === 'p0'}
		>
			<TopRow>
				<Slug>{ticket.slug}</Slug>
				<BadgeRow>
					<PriorityBadge priority={ticket.priority} />
					{interactive && users && onAssign && (
						<CardKebabMenu
							assigneeId={ticket.assignee_id}
							users={users}
							onAssign={onAssign}
							onDelete={onDelete}
							disabled={kebabDisabled}
						/>
					)}
				</BadgeRow>
			</TopRow>

			{interactive ? (
				<SubjectLink
					to={`/tickets/${ticket.slug}`}
					// Stop drag from intercepting the click. Without this, dnd-kit's pointer
					// sensor swallows the navigation.
					onPointerDownCapture={e => e.stopPropagation()}
				>
					{ticket.subject}
				</SubjectLink>
			) : (
				<SubjectText>{ticket.subject}</SubjectText>
			)}

			{ticket.requester_name && (
				<RequesterChip>
					<span aria-hidden="true">●</span>
					<span>{ticket.requester_name}</span>
				</RequesterChip>
			)}

			<BottomRow>
				{ticket.assignee_id && ticket.assignee_name ? (
					<AssigneeAvatar title={ticket.assignee_name}>{initials(ticket.assignee_name)}</AssigneeAvatar>
				) : (
					<UnassignedAvatar aria-label="Unassigned">?</UnassignedAvatar>
				)}
				<Age>{formatTicketAge(ticket.created_at)}</Age>
			</BottomRow>
		</Card>
	);
}

export function TicketCard({ticket, draggable, users, onAssign, onDelete}: Props) {
	const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
		id: ticket.id,
		disabled: !draggable,
	});

	const style: CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const dragProps = draggable ? {...attributes, ...listeners} : {};

	return (
		<DragWrapper ref={setNodeRef} style={style} $draggable={draggable} {...dragProps}>
			<TicketCardPreview
				ticket={ticket}
				interactive
				ariaDisabled={!draggable}
				title={draggable ? undefined : 'You can only move tickets assigned to you.'}
				users={users}
				onAssign={onAssign ? (userId, note) => onAssign(ticket.id, userId, note) : undefined}
				onDelete={onDelete ? () => onDelete(ticket.globalId) : undefined}
				kebabDisabled={!draggable}
			/>
		</DragWrapper>
	);
}
